import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createAction } from '@reduxjs/toolkit';
import { RootState } from 'app/AppStore';
import { CopyIcon } from 'assets/icons';
import { DefaultCopyMessage } from 'components/CopyToClipboardButton';
import StopPropagation from 'components/StopPropagation/StopPropagation';
import { getUserBehaviourEnabled, getUserBehaviourForcedFeedbackEnabled } from 'domains/appContext/AppContext.store';
import { getBasketId } from 'domains/basket/Basket.store';
import { getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { getDHReference, getIAMReference, getPrice, getStockInfo } from 'domains/references';
import {
  getDealerType,
  getEffectiveCommercialLink,
  getUserCountry,
  getUserId,
  getUserSellerBirCode,
  getUserTresorCode,
} from 'domains/user';
import { theme } from 'styles';
import { Icon, notifyTop, WithTooltip } from 'UI';
import { getData } from 'utils';
import { COPY_FEEDBACK_EVENT } from './CopyReferenceFeedback.styled';
import {
  CopyFeedbackTrigger,
  CopyReferenceFeedbackReason,
  CopyReferenceFeedbackPayload,
  CopyReferenceIdentity,
  CopyReferenceClickedPayload,
  COPY_REFERENCE_CLICKED_ACTION_TYPE,
  COPY_REFERENCE_FEEDBACK_REASON_CLICKED_ACTION_TYPE,
} from './CopyReferenceFeedback.types';
import { WhySearchingElsewherePopup } from './WhySearchingElsewherePopup';

const copyReferenceFeedbackReasonClicked = createAction<CopyReferenceFeedbackPayload>(
  COPY_REFERENCE_FEEDBACK_REASON_CLICKED_ACTION_TYPE,
);

export const copyReferenceClicked = createAction<CopyReferenceClickedPayload>(COPY_REFERENCE_CLICKED_ACTION_TYPE);

interface CopyReferenceWithFeedbackProps {
  referenceNumber: string;
}

export const CopyReferenceWithFeedback = ({ referenceNumber }: CopyReferenceWithFeedbackProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isUserBehaviourEnabled = useSelector(getUserBehaviourEnabled);
  const isPopupEnabled = useSelector(getUserBehaviourForcedFeedbackEnabled);
  const [showPopup, setShowPopup] = useState(false);
  const [lastTrigger, setLastTrigger] = useState<CopyFeedbackTrigger>('COPY_ICON');
  const instanceId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const selectionActiveRef = useRef(false);
  const feedbackIdentityRef = useRef<CopyReferenceIdentity>();

  // Tracking data — all resolved from Redux
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const ipn = useSelector(getUserId);
  const basketId = useSelector(getBasketId);
  const commercialLink = useSelector(getEffectiveCommercialLink);
  const country = useSelector(getUserCountry);
  const dealerType = useSelector(getDealerType);
  const tresorCode = useSelector(getUserTresorCode);
  const sellerBirCode = useSelector(getUserSellerBirCode);
  const stockInfo = useSelector((state: RootState) => getStockInfo(state, referenceNumber));
  const priceData = getData(useSelector((state: RootState) => getPrice(state, referenceNumber)));
  const dhRef = getData(
    useSelector((state: RootState) =>
      getDHReference(state, { referenceNumber, vehicleKey: vehicleDetail?.vehicleKey }),
    ),
  );
  const iamRef = getData(
    useSelector((state: RootState) =>
      getIAMReference(state, { referenceNumber, vehicleKey: vehicleDetail?.vehicleKey }),
    ),
  );

  // Snapshot ref — always latest without re-registering the event listener
  const trackingSnapshotRef = useRef<
    Omit<CopyReferenceClickedPayload, 'referenceNumber' | 'trigger' | 'url' | 'timestamp'>
  >();
  useEffect(() => {
    trackingSnapshotRef.current = {
      ipn,
      basketId,
      buyerId: commercialLink?.buyerId,
      buyerRepository: commercialLink?.repository,
      vin: vehicleDetail?.vin,
      vrn: vehicleDetail?.vrn,
      vehicleBrand: vehicleDetail?.vehicleBrand,
      vehicleModel: vehicleDetail?.model,
      country,
      dealerType,
      tresorCode,
      sellerBirCode,
      stockConfirmedQuantity: stockInfo?.data?.confirmedQuantity,
      referenceType: dhRef?.brand ?? iamRef?.brand,
      familyCode: dhRef?.familyCode ?? iamRef?.commercialFamily,
      referenceLabel: dhRef?.name ?? iamRef?.name ?? iamRef?.designation,
      priceVatExcluded: priceData?.garageView?.vatExcludedPrice,
      priceVatIncluded: priceData?.garageView?.vatIncludedPrice,
    };
  });

  const dispatchCopyReferenceClicked = useCallback(
    (trigger: CopyFeedbackTrigger) => {
      if (!isUserBehaviourEnabled || trackingSnapshotRef.current === undefined) return;
      const td = trackingSnapshotRef.current;
      dispatch(
        copyReferenceClicked({
          timestamp: new Date().toISOString(),
          vin: td.vin,
          vrn: td.vrn,
          vehicleBrand: td.vehicleBrand,
          vehicleModel: td.vehicleModel,
          country: td.country,
          dealerType: td.dealerType,
          tresorCode: td.tresorCode,
          ipn: td.ipn,
          basketId: td.basketId,
          buyerId: td.buyerId,
          buyerRepository: td.buyerRepository,
          sellerBirCode: td.sellerBirCode,
          referenceNumber,
          url: globalThis.location.href,
          stockConfirmedQuantity: td.stockConfirmedQuantity,
          priceVatExcluded: td.priceVatExcluded,
          priceVatIncluded: td.priceVatIncluded,
          referenceType: td.referenceType,
          familyCode: td.familyCode,
          referenceLabel: td.referenceLabel,
          trigger,
        }),
      );
    },
    [dispatch, isUserBehaviourEnabled, referenceNumber],
  );

  // Listen for global open event — one popup at a time
  useEffect(() => {
    const handleOpenEvent = (e: Event) => {
      const detail = (e as CustomEvent<{ referenceNumber: string; trigger: CopyFeedbackTrigger; sourceId: string }>)
        .detail;
      const isMyInstance =
        detail.sourceId == null ? detail.referenceNumber === referenceNumber : detail.sourceId === instanceId;
      if (isMyInstance) {
        if (detail.trigger === 'COPY_ICON') {
          navigator.clipboard.writeText(referenceNumber).catch(() => undefined);
          notifyTop('info', DefaultCopyMessage);
          dispatchCopyReferenceClicked('COPY_ICON');
        }
        if (detail.trigger === 'SELECTION') {
          selectionActiveRef.current = true;
        }
        if (isPopupEnabled) {
          feedbackIdentityRef.current = {
            ipn: trackingSnapshotRef.current?.ipn,
            tresorCode: trackingSnapshotRef.current?.tresorCode,
            basketId: trackingSnapshotRef.current?.basketId,
            buyerId: trackingSnapshotRef.current?.buyerId,
            buyerRepository: trackingSnapshotRef.current?.buyerRepository,
          };
          setLastTrigger(detail.trigger);
          setShowPopup(true);
        }
      } else {
        setShowPopup(false);
      }
    };
    document.addEventListener(COPY_FEEDBACK_EVENT, handleOpenEvent);
    return () => document.removeEventListener(COPY_FEEDBACK_EVENT, handleOpenEvent);
  }, [isPopupEnabled, referenceNumber, instanceId, dispatch, dispatchCopyReferenceClicked]);

  // Dispatch copyReferenceClicked when user copies selected text (Ctrl+C or right-click → Copy)
  useEffect(() => {
    const handleCopy = () => {
      if (!selectionActiveRef.current || !isUserBehaviourEnabled || trackingSnapshotRef.current === undefined) return;
      const selectedText = globalThis.getSelection()?.toString().trim() ?? '';
      if (selectedText.length < 4) {
        selectionActiveRef.current = false;
        return;
      }
      selectionActiveRef.current = false;
      // Browser already writes the selection to the clipboard — no manual writeText needed
      dispatchCopyReferenceClicked('SELECTION');
    };
    document.addEventListener('copy', handleCopy);
    return () => document.removeEventListener('copy', handleCopy);
  }, [dispatchCopyReferenceClicked, isUserBehaviourEnabled]);

  // Click-outside detection to close popup
  useEffect(() => {
    if (!showPopup) {
      return undefined;
    }
    const handleDocumentMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [showPopup]);

  const openFeedbackPopup = useCallback(
    (trigger: CopyFeedbackTrigger) => {
      document.dispatchEvent(
        new CustomEvent(COPY_FEEDBACK_EVENT, { detail: { referenceNumber, trigger, sourceId: instanceId } }),
      );
    },
    [referenceNumber, instanceId],
  );

  const handleIconClick = useCallback(() => {
    openFeedbackPopup('COPY_ICON');
  }, [openFeedbackPopup]);

  const handlePopupClose = useCallback(() => {
    setShowPopup(false);
  }, []);

  const handleReasonClick = useCallback(
    (reason: CopyReferenceFeedbackReason) => {
      setShowPopup(false);
      dispatch(
        copyReferenceFeedbackReasonClicked({
          ...feedbackIdentityRef.current,
          referenceNumber,
          reason,
          trigger: lastTrigger,
          timestamp: new Date().toISOString(),
        }),
      );
      notifyTop('success', t('catalog.copy_reference.request_sent.title', 'Request Sent'));
    },
    [dispatch, referenceNumber, lastTrigger, t],
  );

  return (
    <StopPropagation>
      <div
        ref={containerRef}
        style={{ position: 'relative' }}
        data-copy-feedback-id={instanceId}
        data-popup-open={showPopup}
      >
        <WithTooltip title={t('catalog.reference_card.reference_number.copy', 'Copy reference')}>
          <button
            type={'button'}
            onClick={handleIconClick}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              padding: 0,
            }}
          >
            <Icon
              IconComponent={CopyIcon}
              height={16}
              width={16}
              color={theme.color.brand_black}
              hoverFill={theme.color.brand_black_light_120}
              data-cy={'copy-to-clipboard'}
            />
          </button>
        </WithTooltip>
        {showPopup && <WhySearchingElsewherePopup onReasonClick={handleReasonClick} onClose={handlePopupClose} />}
      </div>
    </StopPropagation>
  );
};

export const openCopyReferenceFeedback = (
  referenceNumber: string,
  trigger: CopyFeedbackTrigger = 'SELECTION',
  sourceId?: string,
): void => {
  document.dispatchEvent(new CustomEvent(COPY_FEEDBACK_EVENT, { detail: { referenceNumber, trigger, sourceId } }));
};
